import {
  BarChart2,
  Bell,
  BookOpen,
  Building2,
  CalendarDays,
  CheckSquare,
  FileText,
  Filter,
  GitBranch,
  Globe,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Megaphone,
  Search,
  Settings,
  Share2,
  Sun,
  Tent,
  UserPlus,
  Users,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ModuleAccessId } from '@connexio/shared';

/** Groups mirror `client/src/layouts/navGroups.ts` so the story matches the product's own shape. */
export const SHOWCASE_GROUP_IDS = [
  'overview',
  'crm',
  'marketing',
  'communication',
  'bookings',
  'finance',
  'analytics',
  'platform',
] as const;

export type ShowcaseGroupId = (typeof SHOWCASE_GROUP_IDS)[number];

/** Live previews render a real production component with typed fixture data. */
export type ShowcasePreviewId =
  | 'analytics'
  | 'pipeline'
  | 'contactsTable'
  | 'prospectsTable'
  | 'quotesTable'
  | 'invoicesTable';

export type ShowcaseVisual =
  | { kind: 'component'; preview: ShowcasePreviewId }
  | { kind: 'screenshot'; asset: string }
  | { kind: 'none' };

export interface ShowcaseSection {
  group: ShowcaseGroupId;
  /** Where the module lives in the app — shown as provenance and used by the capture script. */
  route: string;
  icon: LucideIcon;
  visual: ShowcaseVisual;
}

/**
 * Exhaustive over `ModuleAccessId`. Adding a module to `OPTIONAL_MODULE_IDS` or
 * `ALWAYS_ON_MODULE_IDS` in `shared/src/schemas/modules.ts` fails `npm run typecheck`
 * until it has sales material here, so this page cannot drift from the product.
 */
export const SHOWCASE_SECTIONS: Record<ModuleAccessId, ShowcaseSection> = {
  today: {
    group: 'overview',
    route: '/today',
    icon: Sun,
    visual: { kind: 'screenshot', asset: 'today.webp' },
  },
  dashboard: {
    group: 'overview',
    route: '/dashboard',
    icon: LayoutDashboard,
    visual: { kind: 'component', preview: 'analytics' },
  },

  contacts: {
    group: 'crm',
    route: '/contacts',
    icon: Users,
    visual: { kind: 'component', preview: 'contactsTable' },
  },
  companies: {
    group: 'crm',
    route: '/companies',
    icon: Building2,
    visual: { kind: 'none' },
  },
  prospects: {
    group: 'crm',
    route: '/prospects',
    icon: Search,
    visual: { kind: 'component', preview: 'prospectsTable' },
  },
  leads: {
    group: 'crm',
    route: '/leads',
    icon: UserPlus,
    visual: { kind: 'none' },
  },
  deals: {
    group: 'crm',
    route: '/deals',
    icon: GitBranch,
    visual: { kind: 'component', preview: 'pipeline' },
  },
  tasks: {
    group: 'crm',
    route: '/tasks',
    icon: CheckSquare,
    visual: { kind: 'none' },
  },

  campaigns: {
    group: 'marketing',
    route: '/campaigns',
    icon: Megaphone,
    visual: { kind: 'screenshot', asset: 'campaigns.webp' },
  },
  segments: {
    group: 'marketing',
    route: '/segments',
    icon: Filter,
    visual: { kind: 'none' },
  },
  workflows: {
    group: 'marketing',
    route: '/workflows',
    icon: Workflow,
    visual: { kind: 'screenshot', asset: 'workflows.webp' },
  },
  landingPages: {
    group: 'marketing',
    route: '/landing-pages',
    icon: Globe,
    visual: { kind: 'none' },
  },
  social: {
    group: 'marketing',
    route: '/social',
    icon: Share2,
    visual: { kind: 'screenshot', asset: 'social.webp' },
  },

  email: {
    group: 'communication',
    route: '/email',
    icon: Mail,
    visual: { kind: 'screenshot', asset: 'email.webp' },
  },
  notifications: {
    group: 'communication',
    route: '/notifications',
    icon: Bell,
    visual: { kind: 'none' },
  },
  tickets: {
    group: 'communication',
    route: '/tickets',
    icon: LifeBuoy,
    visual: { kind: 'none' },
  },
  knowledgeBase: {
    group: 'communication',
    route: '/kb',
    icon: BookOpen,
    visual: { kind: 'screenshot', asset: 'knowledge-base.webp' },
  },
  documentation: {
    group: 'communication',
    route: '/help-docs',
    icon: BookOpen,
    visual: { kind: 'none' },
  },

  bookings: {
    group: 'bookings',
    route: '/bookings',
    icon: CalendarDays,
    visual: { kind: 'screenshot', asset: 'bookings.webp' },
  },
  stays: {
    group: 'bookings',
    route: '/stays',
    icon: Tent,
    visual: { kind: 'screenshot', asset: 'stays.webp' },
  },

  quotes: {
    group: 'finance',
    route: '/quotes',
    icon: FileText,
    visual: { kind: 'component', preview: 'quotesTable' },
  },
  invoices: {
    group: 'finance',
    route: '/invoices',
    icon: FileText,
    visual: { kind: 'component', preview: 'invoicesTable' },
  },

  analytics: {
    group: 'analytics',
    route: '/analytics',
    icon: BarChart2,
    visual: { kind: 'component', preview: 'analytics' },
  },
  reports: {
    group: 'analytics',
    route: '/reports',
    icon: BarChart2,
    visual: { kind: 'screenshot', asset: 'reports.webp' },
  },

  settings: {
    group: 'platform',
    route: '/settings/organization/modules',
    icon: Settings,
    visual: { kind: 'screenshot', asset: 'settings.webp' },
  },
};

export const SHOWCASE_MODULE_IDS = Object.keys(SHOWCASE_SECTIONS) as ModuleAccessId[];

export interface ShowcaseGroup {
  id: ShowcaseGroupId;
  modules: ModuleAccessId[];
}

/** Groups in presentation order, each holding its modules in registry order. */
export const SHOWCASE_GROUPS: ShowcaseGroup[] = SHOWCASE_GROUP_IDS.map((id) => ({
  id,
  modules: SHOWCASE_MODULE_IDS.filter((moduleId) => SHOWCASE_SECTIONS[moduleId].group === id),
}));

/**
 * Captured screenshots, resolved at build time.
 *
 * Assets live under `src/` rather than `public/` precisely so that their existence is a
 * compile-time fact: a module whose capture has not run yet is simply absent from this map
 * and degrades to a copy-only card, instead of rendering an empty browser frame on a public
 * sales page. Vite also hashes and fingerprints them for free.
 */
const SCREENSHOT_URLS = import.meta.glob<string>('../../assets/showcase/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

/** Resolved URL for a captured asset, or `undefined` when it has not been captured yet. */
export function screenshotUrl(asset: string): string | undefined {
  const match = Object.entries(SCREENSHOT_URLS).find(([path]) => path.endsWith(`/${asset}`));
  return match?.[1];
}

/**
 * Whether a module is *meant* to carry a visual. Editorial checks use this, so they hold
 * whether or not the screenshots happen to be committed in the current working tree.
 */
export function declaresVisual(moduleId: ModuleAccessId): boolean {
  return SHOWCASE_SECTIONS[moduleId].visual.kind !== 'none';
}

/**
 * Modules with a visual that can be rendered *now* get the full-width treatment; the rest
 * appear as compact cards. A declared-but-uncaptured screenshot counts as no visual, so the
 * page degrades to copy rather than leaving an empty frame.
 */
export function isFeatured(moduleId: ModuleAccessId): boolean {
  const { visual } = SHOWCASE_SECTIONS[moduleId];
  if (visual.kind === 'screenshot') return screenshotUrl(visual.asset) !== undefined;
  return declaresVisual(moduleId);
}
